/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { onResize } from '@/util/domUtil'
import { windowEvents } from '@/util/interaction'

/** The single CSS var every bottom-clearance consumer reads. */
export const BOTTOM_STACK_HEIGHT_VAR = '--bottom-stack-height'

/**
 * Distance from the viewport bottom to the top of the TALLEST bottom chrome —
 * the `#bottomStack` pills (chat toolbar + mini player) or the solid transport
 * below them, whichever reaches higher. `max(0, …)` guards a pre-layout/SSR
 * read where the rects are all zero.
 *
 * This is the one measured clearance that replaces the old hand-summed offset
 * web: because it reads the LIVE rect, a hidden child (mini player with no
 * player, the whole toolbar on chat-input-hidden routes) has already collapsed
 * out of the rect, so the value can't strand a hidden element's constant.
 */
export function computeBottomStackHeight(
    stack: HTMLElement,
    footer: HTMLElement | null,
    viewportHeight: number
): number {
    const stackTop = stack.getBoundingClientRect().top
    const footerTop = footer ? footer.getBoundingClientRect().top : stackTop
    return Math.max(0, viewportHeight - Math.min(stackTop, footerTop))
}

/**
 * Observe the bottom stack (and the sibling transport) and publish the measured
 * clearance onto `documentElement` as `--bottom-stack-height`. One-directional:
 * layout → var. Re-measures on either element's resize and on viewport resize
 * (which moves the stack without resizing it). Returns a disposer that stops
 * observing and clears the var.
 */
export function publishBottomStackHeight(
    stack: HTMLElement,
    root: HTMLElement = document.documentElement
): Unsubscribe {
    const { add, dispose } = createDisposer()
    const footer = document.getElementById('footer')
    const publish = () => {
        const height = computeBottomStackHeight(stack, footer, window.innerHeight)
        root.style.setProperty(BOTTOM_STACK_HEIGHT_VAR, `${height}px`)
    }
    add(onResize(stack, publish))
    if (footer) add(onResize(footer, publish))
    add(windowEvents.on('resize', publish))
    add(() => root.style.removeProperty(BOTTOM_STACK_HEIGHT_VAR))
    return dispose
}
